import { TypedDocumentNode, gql } from '@apollo/client';
import { isPast } from 'date-fns';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';

import { TokenName } from 'components/token/TokenName';

import { SectionTitle } from '../ui';
import type { TransactionsOffer_tokenOffer } from './__generated__/index.graphql';

const messages = defineMessages({
  send: {
    id: 'TransactionsHistoryOffer.send',
    defaultMessage: 'You {past, select, true {sent} other {send}}',
  },
  receive: {
    id: 'TransactionsHistoryOffer.receive',
    defaultMessage: 'You {past, select, true {received} other {receive}}',
  },
  fees: {
    id: 'TransactionsHistoryOffer.fees',
    defaultMessage: '{price} fee is applied',
  },
});

type Props = {
  tokenOffer: TransactionsOffer_tokenOffer;
};

export const TransactionsOffer = ({ tokenOffer }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  if (!currentUser || !tokenOffer) return null;

  if (!tokenOffer.sender) {
    return null;
  }

  if (
    (tokenOffer.receiver && !isType(tokenOffer.receiver, 'User')) ||
    !isType(tokenOffer.sender, 'User')
  ) {
    return null;
  }

  const currentUserIsSender = tokenOffer.sender.slug === currentUser.slug;

  const data = currentUserIsSender
    ? {
        otherParty: tokenOffer.receiver,
        sendAmounts: toMonetaryAmount(tokenOffer.senderSide.amounts),
        sendCards: tokenOffer.senderSide.cards,
        receiveCards: tokenOffer.receiverSide.cards,
      }
    : {
        otherParty: tokenOffer.receiver,
        sendAmounts: toMonetaryAmount(tokenOffer.receiverSide.amounts),
        sendCards: tokenOffer.receiverSide.cards,
        receiveCards: tokenOffer.senderSide.cards,
      };
  const offerEnded = isPast(tokenOffer.endDate);

  return (
    <Vertical gap={2}>
      {data.sendCards.length > 0 && (
        <div>
          <SectionTitle>
            <FormattedMessage
              {...messages.send}
              values={{ past: offerEnded }}
            />
          </SectionTitle>
          {!!data.sendAmounts.isPositive && (
            <AmountWithConversion
              monetaryAmount={data.sendAmounts}
              primaryCurrency={Currency.ETH}
            />
          )}

          {data.sendCards.map(sendCard => (
            <TokenName key={sendCard.slug} card={sendCard} withLink />
          ))}
        </div>
      )}
      {data.receiveCards.length > 0 && (
        <div>
          <SectionTitle>
            <FormattedMessage
              {...messages.receive}
              values={{ past: offerEnded }}
            />
          </SectionTitle>
          {data.receiveCards.map(receiveCard => (
            <TokenName key={receiveCard.slug} card={receiveCard} withLink />
          ))}
        </div>
      )}
    </Vertical>
  );
};

TransactionsOffer.fragments = {
  tokenOffer: gql`
    fragment TransactionsOffer_tokenOffer on TokenOffer {
      id
      endDate
      sender {
        ... on User {
          slug
          suspended
          nickname
        }
        ... on AnonymousUser {
          id
        }
      }
      receiver {
        ... on User {
          slug
          suspended
          nickname
        }
        ... on AnonymousUser {
          id
        }
        ... on AnonymousUser {
          id
        }
      }
      senderSide {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
        cards: anyCards {
          slug
          ...TokenName_anyCard
        }
      }
      receiverSide {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
        cards: anyCards {
          slug
          ...TokenName_anyCard
        }
      }
    }
    ${TokenName.fragments.anyCard}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<TransactionsOffer_tokenOffer>,
};
